import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

interface BotUser {
  id: string;
  channel: string;
  identifier: string;
  name: string;
  email: string;
  phone: string;
  messageCount: number;
  lastSeen: number | null;
  firstSeen: number | null;
}

interface Props {
  bot: { id: string; businessName?: string };
}

function fmt(ts: number | null) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function channelBadge(ch: string) {
  const map: Record<string, { bg: string; color: string }> = {
    WhatsApp: { bg: '#0c3a24', color: '#25d366' },
    'Web Chat': { bg: '#1e2a45', color: '#60a5fa' },
    Facebook: { bg: '#1a2040', color: '#818cf8' },
    Voice: { bg: '#2a1a1a', color: '#f87171' },
  };
  const s = map[ch] || { bg: '#1e2330', color: '#94a3b8' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>
      {ch}
    </span>
  );
}

function exportCSV(users: BotUser[], botName: string) {
  const rows = [
    ['Name', 'Email', 'Phone / Identifier', 'Channel', 'Messages', 'First Seen', 'Last Seen'],
    ...users.map(u => [
      u.name || '—',
      u.email || '—',
      u.phone || u.identifier,
      u.channel,
      String(u.messageCount),
      fmt(u.firstSeen),
      fmt(u.lastSeen),
    ]),
  ];
  const csv = rows.map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${botName.replace(/\s+/g, '_')}_users_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(users: BotUser[], botName: string) {
  const rows = users
    .map(
      u => `<tr>
        <td>${u.name || '—'}</td>
        <td>${u.email || '—'}</td>
        <td>${u.phone || u.identifier}</td>
        <td>${u.channel}</td>
        <td style="text-align:center">${u.messageCount}</td>
        <td>${fmt(u.firstSeen)}</td>
        <td>${fmt(u.lastSeen)}</td>
      </tr>`,
    )
    .join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>${botName} – User Report</title>
    <style>
      body{font-family:sans-serif;padding:24px;color:#111}
      h1{font-size:18px;margin-bottom:4px}
      p.sub{color:#666;font-size:12px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{background:#1e2330;color:#fff;padding:8px 10px;text-align:left}
      td{padding:7px 10px;border-bottom:1px solid #eee}
      tr:nth-child(even) td{background:#f8f8f8}
    </style></head><body>
    <h1>${botName} – User Contact Report</h1>
    <p class="sub">Generated ${new Date().toLocaleString()} · ${users.length} users</p>
    <table>
      <thead><tr><th>Name</th><th>Email</th><th>Phone/ID</th><th>Channel</th><th>Messages</th><th>First Seen</th><th>Last Seen</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    </body></html>`);
  w.document.close();
  w.print();
}

export default function UsersPanel({ bot }: Props) {
  const [users, setUsers] = useState<BotUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [sortCol, setSortCol] = useState<'messageCount' | 'lastSeen' | 'name'>('lastSeen');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await apiFetch(`/api/bot-users/${bot.id}`);
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        setUsers(data.users || []);
      } catch (e: any) {
        setError(e.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, [bot.id]);

  const channels = ['all', ...Array.from(new Set(users.map(u => u.channel)))];

  const filtered = users
    .filter(u => channelFilter === 'all' || u.channel === channelFilter)
    .filter(u => {
      const q = search.toLowerCase();
      return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone.includes(q) || u.identifier.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let av: any = a[sortCol] ?? 0;
      let bv: any = b[sortCol] ?? 0;
      if (sortCol === 'name') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      return sortDir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
    });

  function toggleSort(col: typeof sortCol) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  const hdr = { color: '#64748b', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', padding: '0 12px 10px', cursor: 'pointer', userSelect: 'none' as const, whiteSpace: 'nowrap' as const };
  const cell = { padding: '12px', borderBottom: '1px solid #1e2330', fontSize: '13px', color: '#e2e8f0', verticalAlign: 'top' as const };

  if (loading) return <div style={{ color: '#64748b', padding: '40px', textAlign: 'center' }}>Loading users…</div>;
  if (error) return <div style={{ color: '#f87171', padding: '20px' }}>{error}</div>;

  return (
    <div>
      {/* Stat + export bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ background: '#15181f', border: '1px solid #1e2330', borderRadius: '10px', padding: '12px 20px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#e2e8f0' }}>{users.length.toLocaleString()}</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Total users</div>
          </div>
          <div style={{ background: '#15181f', border: '1px solid #1e2330', borderRadius: '10px', padding: '12px 20px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#e2e8f0' }}>
              {users.reduce((s, u) => s + u.messageCount, 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Total messages</div>
          </div>
          <div style={{ background: '#15181f', border: '1px solid #1e2330', borderRadius: '10px', padding: '12px 20px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#e2e8f0' }}>
              {users.filter(u => u.lastSeen && u.lastSeen > Date.now() - 7 * 86400_000).length}
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Active last 7d</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => exportCSV(filtered, bot.businessName || bot.id)}
            style={{ background: '#1e2330', border: '1px solid #2d3748', color: '#94a3b8', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
            ⬇ CSV
          </button>
          <button onClick={() => exportPDF(filtered, bot.businessName || bot.id)}
            style={{ background: '#5B4FE8', border: 'none', color: '#fff', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
            🖨 PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone…"
          style={{ flex: 1, minWidth: '200px', background: '#15181f', border: '1px solid #1e2330', borderRadius: '8px', color: '#e2e8f0', padding: '8px 12px', fontSize: '13px', outline: 'none' }} />
        <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}
          style={{ background: '#15181f', border: '1px solid #1e2330', borderRadius: '8px', color: '#94a3b8', padding: '8px 12px', fontSize: '13px', outline: 'none' }}>
          {channels.map(c => <option key={c} value={c}>{c === 'all' ? 'All channels' : c}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: '#475569', textAlign: 'center', padding: '40px' }}>
          {users.length === 0 ? 'No users have messaged this bot yet.' : 'No users match your search.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#15181f', borderRadius: '12px', border: '1px solid #1e2330' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e2330' }}>
                <th style={{ ...hdr, paddingLeft: '16px' }} onClick={() => toggleSort('name')}>
                  Name {sortCol === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={hdr}>Email</th>
                <th style={hdr}>Phone / ID</th>
                <th style={hdr}>Channel</th>
                <th style={{ ...hdr, textAlign: 'center' }} onClick={() => toggleSort('messageCount')}>
                  Messages {sortCol === 'messageCount' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th style={hdr}>First seen</th>
                <th style={hdr} onClick={() => toggleSort('lastSeen')}>
                  Last seen {sortCol === 'lastSeen' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ transition: 'background 0.1s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1a1f2e')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...cell, paddingLeft: '16px', fontWeight: 600 }}>{u.name || <span style={{ color: '#475569' }}>—</span>}</td>
                  <td style={cell}>{u.email || <span style={{ color: '#475569' }}>—</span>}</td>
                  <td style={{ ...cell, color: '#94a3b8', fontFamily: 'monospace', fontSize: '12px' }}>{u.phone || u.identifier}</td>
                  <td style={cell}>{channelBadge(u.channel)}</td>
                  <td style={{ ...cell, textAlign: 'center', fontWeight: 700, color: u.messageCount > 0 ? '#5B4FE8' : '#475569' }}>{u.messageCount}</td>
                  <td style={{ ...cell, color: '#64748b', fontSize: '12px' }}>{fmt(u.firstSeen)}</td>
                  <td style={{ ...cell, color: '#64748b', fontSize: '12px' }}>{fmt(u.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '10px 16px', color: '#475569', fontSize: '12px', borderTop: '1px solid #1e2330' }}>
            Showing {filtered.length} of {users.length} users
          </div>
        </div>
      )}
    </div>
  );
}
